import { lazy, Suspense, useEffect, useMemo, useRef } from "react";
import { ClerkProvider, SignIn, SignUp, useAuth } from "@clerk/react";
import { shadcn } from "@clerk/themes";
import { useTheme } from "next-themes";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Redirect, Route, Switch, Router as WouterRouter, useLocation } from "wouter";
import { Layout } from "@/components/Layout";
import { AppLoading } from "@/components/AppLoading";
import { HomeNavRail } from "@/components/HomeNavRail";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy-loaded per route so the initial bundle only contains what the first
// screen actually needs — each of these becomes its own chunk, fetched on
// navigation instead of all being bundled into one large upfront download.
const Inbox = lazy(() => import("@/pages/Inbox"));
const SearchPage = lazy(() => import("@/pages/Search"));
const NotFound = lazy(() => import("@/pages/not-found"));
const Welcome = lazy(() => import("@/pages/Welcome"));
const Waitlist = lazy(() => import("@/pages/Waitlist"));
const RequestConnector = lazy(() => import("@/pages/RequestConnector"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
// Rules/Connect/Settings/Account used to each be their own lazy-loaded
// route — now they're tab content rendered inside UnifiedSettings (see the
// user-menu simplification comment there), so only this one is routed to
// directly; the other three are still real files, just no longer pages in
// their own right (no default export — see each file's own comment).
const UnifiedSettings = lazy(() => import("@/pages/UnifiedSettings"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

// publishableKeyFromHost() exists for genuine multi-domain/satellite setups
// where a different Clerk domain is registered per host and the right key
// has to be re-derived per request. This app has exactly one fixed
// production domain (assistio.co.uk) — using that helper here actively
// broke production: it only preserves the real key as-is for *development*
// instances, and for a pk_live_ key it instead reconstructs one from
// whatever host the browser is currently on, which produced
// "clerk.www.assistio.co.uk" (not a real Clerk domain) the moment this site
// was loaded via the www subdomain instead of the bare domain Clerk actually
// has on file. Just use the configured key directly — there's no second
// domain to resolve between.
const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

function stripBase(path: string) {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

// Clerk's `shadcn` preset reads raw `var(--card)`-style tokens, but this
// app's CSS variables are unitless HSL triplets meant to be wrapped in
// hsl(...) — so the preset's own token references resolve to nothing.
// Overriding `variables` directly with hsl() strings sidesteps that, and
// computing them from the current theme (rather than a fixed light palette)
// is what makes every Clerk-rendered screen — sign-in, sign-up, the account
// page — actually follow dark mode instead of always rendering light.
function buildClerkAppearance(isDark: boolean) {
  return {
    theme: shadcn,
    cssLayerName: "clerk",
    options: {
      logoPlacement: "inside" as const,
      logoLinkUrl: basePath || "/",
      // The full wordmark (not just the icon mark) — more recognizable as
      // "our logo" on a page a user only sees occasionally, and swapped per
      // theme since the light variant's dark text disappears on a dark card.
      logoImageUrl: `${window.location.origin}${basePath}/${isDark ? "appetised-logo-dark.png" : "appetised-logo-light.png"}`,
      socialButtonsPlacement: "top" as const,
      socialButtonsVariant: "blockButton" as const,
    },
    // appetised brand palette — mirrors src/index.css's :root/.dark tokens
    // (Clerk's own `variables` need real hsl() strings, not var() refs, see
    // the comment above this function), so this needed the same manual
    // update as index.css rather than picking the new colors up for free.
    variables: {
      colorPrimary: isDark ? "hsl(222 62% 66%)" : "hsl(226 72% 39%)",
      colorForeground: isDark ? "hsl(40 45% 90%)" : "hsl(224 65% 15%)",
      colorMutedForeground: isDark ? "hsl(219 27% 67%)" : "hsl(219 22% 45%)",
      colorDanger: isDark ? "hsl(0 70% 55%)" : "hsl(0 78% 55%)",
      colorBackground: isDark ? "hsl(224 64% 12%)" : "hsl(222 38% 97%)",
      colorInput: isDark ? "hsl(224 35% 18%)" : "hsl(220 30% 94%)",
      colorInputForeground: isDark ? "hsl(40 45% 90%)" : "hsl(224 65% 15%)",
      colorNeutral: isDark ? "hsl(224 45% 25%)" : "hsl(220 30% 87%)",
      fontFamily: "Archivo, sans-serif",
      borderRadius: "0.5rem",
    },
    elements: {
      rootBox: "w-full flex justify-center",
      cardBox: "bg-card rounded-2xl overflow-hidden",
      card: "!shadow-none !border-0 !bg-transparent !rounded-none",
      footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
      headerTitle: "text-foreground",
      headerSubtitle: "text-muted-foreground",
      socialButtonsBlockButtonText: "text-foreground",
      formFieldLabel: "text-foreground",
      footerActionLink: "text-primary",
      footerActionText: "text-muted-foreground",
      dividerText: "text-muted-foreground",
      formFieldInput: "bg-background text-foreground !border-2 !border-border",
      formButtonPrimary: "bg-primary hover:bg-primary/90 !text-white",
      socialButtonsBlockButton: "border-border bg-background hover:bg-secondary",
    },
  };
}

function AuthLoading() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background">
      <AppLoading />
    </div>
  );
}

const AUTH_CARD_APPEARANCE = { elements: { cardBox: "w-[440px] max-w-full border border-border rounded-2xl bg-card overflow-hidden" } };

function AuthPages() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4 py-8">
      <Switch>
        <Route path="/sign-in/*?">
          <SignIn routing="path" path={`${basePath}/sign-in`} signUpUrl={`${basePath}/sign-up`} appearance={AUTH_CARD_APPEARANCE} />
        </Route>
        <Route path="/sign-up/*?">
          <SignUp routing="path" path={`${basePath}/sign-up`} signInUrl={`${basePath}/sign-in`} appearance={AUTH_CARD_APPEARANCE} />
        </Route>
      </Switch>
    </div>
  );
}

function ClerkQueryClientCacheInvalidator() {
  const { userId } = useAuth();
  const previousUserId = useRef<string | null | undefined>(undefined);
  const client = useQueryClient();

  useEffect(() => {
    if (previousUserId.current !== undefined && previousUserId.current !== userId) {
      client.clear();
    }
    previousUserId.current = userId;
  }, [client, userId]);

  return null;
}

function HomeRoute() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <AuthLoading />;
  return isSignedIn ? <Workspace /> : <Welcome />;
}

function ProtectedWorkspace() {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded) return <AuthLoading />;
  if (!isSignedIn) return <Redirect to="/" />;

  return (
    <Layout>
      <Switch>
        <Route path="/search" component={SearchPage} />
        {/* Rules/Connections/Account are tabs inside the unified Settings
            page now (see its own comment) — these three routes exist purely
            so an old bookmark/link to one of them still lands somewhere
            useful instead of 404ing. */}
        <Route path="/rules"><Redirect to="/settings?tab=rules" /></Route>
        <Route path="/connect"><Redirect to="/settings?tab=connections" /></Route>
        <Route path="/account"><Redirect to="/settings?tab=account" /></Route>
        <Route path="/wellbeing"><Redirect to="/settings?tab=wellbeing" /></Route>
        <Route path="/settings">
          <HomeNavRail><UnifiedSettings /></HomeNavRail>
        </Route>
        <Route component={NotFound} />
      </Switch>
    </Layout>
  );
}

// Every one of these renders the same Inbox component — the path itself is
// what tells Inbox which sub-view to show (see the useRoute()/useLocation()
// calls at the top of Inbox.tsx). This is what gives lists/tasks/docs/
// calendar/AI-chat/Slack channels real, shareable URLs and makes the
// browser's native back/forward buttons work for navigating between them.
// Shared with Router() below: `wouter`'s <Switch> only tries the next nested
// <Switch> (inside whichever component matched there) once a top-level
// pattern matches, so every one of these also needs its own entry in the
// outer Switch, routed to the same HomeRoute — otherwise it falls through to
// the catch-all ProtectedWorkspace route and 404s before getting here.
const INBOX_SUB_PATHS = ["/list/:id", "/tasks", "/docs", "/docs/:id", "/calendar", "/analytics", "/ai-chat", "/slack/:channel", "/teams/:conversation", "/chat/:channel", "/folder/:key"];

function Workspace() {
  return (
    <Layout>
      <Switch>
        <Route path="/" component={Inbox} />
        {INBOX_SUB_PATHS.map((path) => (
          <Route key={path} path={path} component={Inbox} />
        ))}
      </Switch>
    </Layout>
  );
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={HomeRoute} />
      {INBOX_SUB_PATHS.map((path) => (
        <Route key={path} path={path} component={HomeRoute} />
      ))}
      <Route path="/waitlist" component={Waitlist} />
      <Route path="/request-connector" component={RequestConnector} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/sign-in/*?" component={AuthPages} />
      <Route path="/sign-up/*?" component={AuthPages} />
      <Route component={ProtectedWorkspace} />
    </Switch>
  );
}

function App() {
  const [, setLocation] = useLocation();
  const { resolvedTheme } = useTheme();
  const clerkAppearance = useMemo(() => buildClerkAppearance(resolvedTheme === "dark"), [resolvedTheme]);

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      localization={{
        signIn: { start: { title: "Welcome back", subtitle: "Sign in to access your unified inbox" } },
        signUp: { start: { title: "Create your appetised account", subtitle: "Bring your conversations together" } },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkQueryClientCacheInvalidator />
        <ErrorBoundary>
          <Suspense fallback={<AuthLoading />}>
            <Router />
          </Suspense>
        </ErrorBoundary>
        <Toaster />
      </QueryClientProvider>
    </ClerkProvider>
  );
}

export default function RootApp() {
  return (
    <TooltipProvider>
      <WouterRouter base={basePath}>
        <App />
      </WouterRouter>
    </TooltipProvider>
  );
}